import React, { useState, useContext } from 'react';
import { Box, IconButton, Menu, MenuItem, Avatar, Typography } from '@mui/material';
import { Logout, AccountCircle, Edit, Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import Auth from '../utils/auth';
import ClientBloomFeedbackForm from '../components/Feedback/feedback';
import Modal from '../components/ui/Modal';
import { UserContext } from '../utils/UserContext';

const SUPER_ADMIN_WORKSPACE_IDS = [6, 41];

const Header = () => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
    const mobileOpen = Boolean(mobileAnchorEl);
    const [showFeedback, setShowFeedback] = useState(false);
    const { user: contextUser } = useContext(UserContext);
    const user = contextUser;

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        Auth.logout();
        navigate('/login');
    };

    const isSuperAdmin = SUPER_ADMIN_WORKSPACE_IDS.includes(Number(Auth.getWorkspaceId()));

    const navLinks = [
        { label: 'Dashboard', href: '/' },
        { label: 'Clients', href: '/clients' },
        { label: 'BI Agent', to: '/bi-agent?nav=1' },
        ...(user?.admin ? [{ label: 'Admin', href: '/admin-panel' }] : []),
        ...(user && isSuperAdmin ? [{ label: 'Super Admin', href: '/super-admin' }] : []),
    ];

    return (
        <header className="bg-canvas/80 backdrop-blur-sm border-b border-purple-100">
            <div className="mx-auto flex max-w-layout items-center gap-4 px-4 py-3 sm:px-6">
                <img
                    src="/cb_logo.png"
                    alt="ClientBloom Logo"
                    className="h-9 sm:h-11 flex-shrink-0"
                />

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-7 font-ui font-medium">
                    {navLinks.map((link) =>
                        link.to ? (
                            <Link
                                key={link.label}
                                to={link.to}
                                className="text-ink/60 transition-colors hover:text-ink"
                            >
                                {link.label}
                            </Link>
                        ) : (
                            <a
                                key={link.label}
                                href={link.href}
                                className="text-ink/60 transition-colors hover:text-ink"
                            >
                                {link.label}
                            </a>
                        )
                    )}
                </nav>

                {/* Right cluster */}
                <Box className="ml-auto flex items-center gap-2 sm:gap-4">
                    {user && (
                        <>
                            <IconButton onClick={() => setShowFeedback(true)} size="small">
                                <Avatar sx={{ bgcolor: '#fff', width: 44, height: 44 }}>
                                    <img src="/feedback_form_icon.png" alt="Feedback" style={{ width: 44, height: 44 }} />
                                </Avatar>
                            </IconButton>
                            <IconButton onClick={handleMenuOpen} size="small">
                                <Avatar
                                    alt={user?.email}
                                    src={user?.profileImage?.url}
                                    sx={{ width: 40, height: 40 }}
                                >
                                    <AccountCircle fontSize="large" />
                                </Avatar>
                            </IconButton>
                        </>
                    )}

                    {/* Mobile hamburger */}
                    <IconButton
                        className="md:!hidden"
                        onClick={(e) => setMobileAnchorEl(e.currentTarget)}
                        size="small"
                        aria-label="Open navigation menu"
                    >
                        <MenuIcon sx={{ color: '#291B3B' }} />
                    </IconButton>
                </Box>

                {/* Profile menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {user && (
                        <MenuItem disabled>
                            <Typography variant="body2">{user.email}</Typography>
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            navigate('/edit-profile');
                        }}
                    >
                        <Edit fontSize="small" sx={{ marginRight: 1 }} />
                        Edit Profile
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                        <Logout fontSize="small" sx={{ marginRight: 1 }} />
                        Logout
                    </MenuItem>
                </Menu>

                {/* Mobile nav menu */}
                <Menu
                    anchorEl={mobileAnchorEl}
                    open={mobileOpen}
                    onClose={() => setMobileAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {navLinks.map((link) => (
                        <MenuItem
                            key={link.label}
                            onClick={() => {
                                setMobileAnchorEl(null);
                                navigate(link.to || link.href);
                            }}
                        >
                            {link.label}
                        </MenuItem>
                    ))}
                </Menu>

                <Modal open={showFeedback} onClose={() => setShowFeedback(false)}>
                    <ClientBloomFeedbackForm />
                </Modal>
            </div>
        </header>
    );
};

export default Header;
